/**
 * Bild auswählen → lokale Vorschau → hochladen bzw. verwerfen; vorhandenes Bild entfernen.
 * PNG/JPEG/GIF/WebP (animiert erlaubt, Vertrag 0.6).
 */
import { ImagePlus, Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** Prüft Typ und Größe; liefert Fehlermeldung oder null. */
export function checkImage(file: File, maxBytes: number): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return 'Nur PNG, JPEG, GIF oder WebP.';
  if (maxBytes > 0 && file.size > maxBytes) return `Datei zu groß (höchstens ${formatBytes(maxBytes)}).`;
  return null;
}

interface Props {
  label: string;
  kind: 'avatar' | 'banner';
  currentUrl: string | null;
  /** Lädt hoch; Fehler werden angezeigt. */
  onUpload: (file: File) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  /** Ausgewählte, noch nicht hochgeladene Datei (für Live-Vorschau beim Aufrufer). */
  onPreview?: (url: string | null) => void;
  testId: string;
  disabled?: boolean;
}

export function ImageUpload({ label, kind, currentUrl, onUpload, onRemove, onPreview, testId, disabled }: Props) {
  const maxBytes = useAppStore((s) => s.limits.maxAvatarBytes);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const pick = (f: File | undefined) => {
    if (!f) return;
    const err = checkImage(f, maxBytes);
    setError(err);
    if (err) return;
    const url = URL.createObjectURL(f);
    setFile(f);
    setPreview(url);
    onPreview?.(url);
  };
  const discard = () => {
    setFile(null);
    setPreview(null);
    onPreview?.(null);
    if (inputRef.current) inputRef.current.value = '';
  };
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await onUpload(file);
      discard();
      toast.success(`${label} gespeichert`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await onRemove();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const shown = preview ?? currentUrl;
  return (
    <div className="lf-image-upload" data-kind={kind} data-testid={testId}>
      <div className="label">{label}</div>
      <div className="lf-image-upload-row">
        <div className={`lf-image-thumb lf-image-thumb-${kind}`} data-testid={`${testId}-preview`} data-pending={preview ? 'true' : undefined}>
          {shown ? <img src={shown} alt={`${label} – Vorschau`} /> : <ImagePlus size={22} aria-hidden="true" />}
        </div>
        <div className="stack" style={{ gap: '0.4rem' }}>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={IMAGE_TYPES.join(',')}
            className="sr-only"
            onChange={(e) => pick(e.target.files?.[0])}
            disabled={disabled || busy}
            data-testid={`${testId}-input`}
          />
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {file ? (
              <>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void upload()} disabled={busy} data-testid={`${testId}-save`}>
                  Hochladen
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={discard} disabled={busy} data-testid={`${testId}-discard`}>
                  Verwerfen
                </button>
              </>
            ) : (
              <>
                <label htmlFor={inputId} className="btn btn-sm" aria-disabled={disabled || busy} data-testid={`${testId}-choose`}>
                  {currentUrl ? 'Ändern' : 'Bild wählen'}
                </label>
                {currentUrl && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => void remove()} disabled={disabled || busy} data-testid={`${testId}-remove`}>
                    <Trash2 size={14} aria-hidden="true" /> Entfernen
                  </button>
                )}
              </>
            )}
          </div>
          <span className="field-hint">PNG, JPEG, GIF oder WebP (animiert erlaubt){maxBytes > 0 ? `, bis ${formatBytes(maxBytes)}` : ''}.</span>
          {error && (
            <span className="error-text" role="alert" data-testid={`${testId}-error`}>
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
